import React, { useState, useEffect, useRef, useMemo } from 'react';
import useAuthStore from '../store/authStore';
import { useParams } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { FaPencilAlt, FaStar, FaUser, FaShoppingCart } from 'react-icons/fa';
import ProductCard from '../components/ProductCard';
import ProductCardSkeleton from '../components/ProductCardSkeleton';
import { FaYoutube, FaInstagram } from 'react-icons/fa';
import ReviewGrid from '../components/reviews/ReviewGrid';
import SEOHead from '../components/SEOHead';
import { apiGet, apiPost, apiPut, apiPatch, getImageUrl } from '../utils/api';
import { useDarkMode } from '../contexts/DarkModeContext';
import useUserOnlineStatus from '../components/chat/hooks/useUserOnlineStatus';
import { motion } from 'framer-motion';

// Minimal production-only client error reporter
const safeReportError = async (message, context = {}) => {
  try {
    if (process.env.NODE_ENV !== 'production') return;
    const payload = {
      message: String(message || 'unknown_error'),
      context: { location: typeof window !== 'undefined' ? (window.location?.pathname || '/') : '/', ...context },
      ts: Date.now(),
    };
    await fetch('/api/client-logs', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    });
  } catch (_) {}
};
// function getUser() {
//   return JSON.parse(localStorage.getItem('user')) || {};
// }

export default function ProfilePage() {
  const { username } = useParams();
  const { isDarkMode } = useDarkMode();
  const [tab, setTab] = useState('summary');
  const { user: storeUser, setUser: setAuthUser } = useAuthStore();
  const [seoSettings, setSeoSettings] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [reviewStats, setReviewStats] = useState({ averageRating: 0, totalReviews: 0 });

  // Fetch SEO settings for profile page
  useEffect(() => {
    const fetchSeoSettings = async () => {
      try {
        const response = await apiGet('/api/page-seo/page/profile');
        if (response.ok) {
          const data = await response.json();
          setSeoSettings(data);
        }
      } catch (error) {
        safeReportError('profile_seo_settings_fetch_failed');
      }
    };

    fetchSeoSettings();
  }, []);

  useEffect(() => {
    apiGet('/api/seo?path=/profile')
      .then(res => res.json())
      .then(data => setSeoSettings(data))
      .catch(() => safeReportError('profile_seo_settings_load_failed'));
  }, []);

  const isOwner = (!username && storeUser) || (username && storeUser && username === storeUser.username);
  const [user, setUser] = useState(isOwner ? storeUser : {});
  const [cover, setCover] = useState(isOwner ? (storeUser?.cover || '/images/default-cover.svg') : '/images/default-cover.svg');
  // const [coverFile, setCoverFile] = useState(null);
  // Modern avatar generator (same style as ChatPanel and ProductView)
  const generateModernAvatar = (username) => {
    const name = username || 'User';
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=0f0f0f&color=ffffff&rounded=true&size=200`;
  };
  const [avatar, setAvatar] = useState(isOwner ? (storeUser?.avatar || generateModernAvatar(storeUser?.username)) : generateModernAvatar('User'));
  // const [avatarFile, setAvatarFile] = useState(null);
  const [hoverAvatar, setHoverAvatar] = useState(false);
  const [hoverCover, setHoverCover] = useState(false);

  // Get user online status for public profiles
  const userForPresence = !isOwner && user ? {
    uid: user.firebaseUid || user.uid,
    email: user.email
  } : null;
  const { online: userOnline, lastSeen: userLastSeen } = useUserOnlineStatus(userForPresence);


  const [products, setProducts] = useState([]);
  const [listingStatus, setListingStatus] = useState('active');
  const [loading, setLoading] = useState(false);
  // const [error, setError] = useState('');
  const [showEditDesc, setShowEditDesc] = useState(false);
  const [descInput, setDescInput] = useState(user.description || '');
  const [showEditName, setShowEditName] = useState(false);
  const [nameInput, setNameInput] = useState(user.username || '');
  const [savingDescription, setSavingDescription] = useState(false);
  const [checkingUsername, setCheckingUsername] = useState(false);
  const [usernameAvailable, setUsernameAvailable] = useState(null);
  const [usernameError, setUsernameError] = useState('');
  const [savingUsername, setSavingUsername] = useState(false);
  
  // Verification popup state
  const [showVerificationPopup, setShowVerificationPopup] = useState(false);
  
  // Caching constants
  const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes
  const purchasesControllerRef = useRef(null);
  const salesControllerRef = useRef(null);
  const [sales, setSales] = useState([]);
  const uniqueStatuses = Array.from(new Set(sales.map(s => (s.status || '').toLowerCase()))).filter(Boolean);
  const salesStatuses = [
    { key: 'all', label: 'All' },
    ...uniqueStatuses.map(status => ({
      key: status,
      label: status.charAt(0).toUpperCase() + status.slice(1)
    }))
  ];
  const [salesStatus, setSalesStatus] = useState('all');
  const [purchases, setPurchases] = useState([]);
  const uniquePurchaseStatuses = Array.from(new Set(purchases.map(s => (s.status || '').toLowerCase()))).filter(Boolean);
  const purchaseStatuses = [
    { key: 'all', label: 'All' },
    ...uniquePurchaseStatuses.map(status => ({
      key: status,
      label: status.charAt(0).toUpperCase() + status.slice(1)
    }))
  ];
  const [purchaseStatus, setPurchaseStatus] = useState('all');
  const [loadingPurchases, setLoadingPurchases] = useState(false);
  const [errorPurchases, setErrorPurchases] = useState('');
  const [productsFetched, setProductsFetched] = useState(false);
  // Remove demoSales and add real sales state
  const [loadingSales, setLoadingSales] = useState(false);
  const [errorSales, setErrorSales] = useState('');
  // Add state for order modal
  const [orderModal, setOrderModal] = useState(null);
  // Add pagination state at the top of the component
  const [listingsToShow, setListingsToShow] = useState(6);
  const [purchasesToShow, setPurchasesToShow] = useState(6);
  const [productSlugCache, setProductSlugCache] = useState({});

  // Build product path preferring slug when present; fallback to id
  const getProductPath = (obj) => {
    const slug = obj?.product_slug || obj?.slug || obj?.seoSlug || obj?.handle || obj?.slugifiedTitle;
    const id = obj?.product_id || obj?._id || obj?.id;
    const tail = slug ? encodeURIComponent(slug) : id;
    return tail ? `/product/${tail}` : '#';
  };

  const openProductLink = async (obj, event) => {
    try {
      if (event) event.preventDefault();
      const slugFromObj = obj?.product_slug || obj?.slug || obj?.seoSlug || obj?.handle || obj?.slugifiedTitle;
      const id = obj?.product_id || obj?._id || obj?.id;
      if (slugFromObj) {
        window.open(`/product/${encodeURIComponent(slugFromObj)}`, '_blank', 'noopener');
        return;
      }
      if (id && productSlugCache[id]) {
        window.open(`/product/${encodeURIComponent(productSlugCache[id])}`, '_blank', 'noopener');
        return;
      }
      if (id) {
        const res = await apiGet(`/api/products/${id}`);
        if (res.ok) {
          const data = await res.json();
          const slug = data?.slug || data?.seoSlug || data?.handle || data?.slugifiedTitle;
          if (slug) {
            setProductSlugCache(prev => ({ ...prev, [id]: slug }));
            window.open(`/product/${encodeURIComponent(slug)}`, '_blank', 'noopener');
            return;
          }
        }
        // Fallback to id if slug not found
        window.open(`/product/${id}`,'_blank','noopener');
      }
    } catch {
      const id = obj?.product_id || obj?._id || obj?.id;
      if (id) window.open(`/product/${id}`,'_blank','noopener');
    }
  };
  const [salesToShow, setSalesToShow] = useState(6);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationType, setNotificationType] = useState('error');

  // Handle cover image upload
  const handleCoverChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // setCoverFile(file);
      const reader = new FileReader();
      reader.onload = (ev) => {
        setCover(ev.target.result);
        // Save to localStorage and update user
        const updatedUser = { ...user, cover: ev.target.result };
        setUser(updatedUser);
        localStorage.setItem('user', JSON.stringify(updatedUser));
      };
      reader.readAsDataURL(file);
    }
  };

  // Show notification function
  // const showNotificationMessage = (message, type = 'error') => {
  //   setNotificationMessage(message);
  //   setNotificationType(type);
  //   setShowNotification(true);
  //   setTimeout(() => setShowNotification(false), 5000);
  // };

  // Handle avatar image upload
  const handleAvatarChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      // setAvatarFile(file);
      const reader = new FileReader();
      reader.onload = async (ev) => {
        try {
          const dataUrl = ev.target.result;
          setAvatar(dataUrl);
          const approxBytes = Math.floor((dataUrl.length * 3) / 4);
          // Upload avatar to backend
          let lsUser = null;
          try { lsUser = JSON.parse(localStorage.getItem('user')); } catch (_) {}
          const response = await apiPatch('/api/auth/profile', { avatar: dataUrl });
          if (!response.ok) {
            const text = await response.text().catch(() => '');
            throw new Error('Failed to update avatar');
          }
          const updatedUser = await response.json();
          setUser(updatedUser);
          localStorage.setItem('user', JSON.stringify(updatedUser));
        } catch (err) {
          safeReportError('profile_avatar_update_failed');
        }
      };
      reader.readAsDataURL(file);
    }
  };

  // Format registration date
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  };

  // Check username availability
  const checkUsernameAvailability = async (username) => {
    if (!username || username.trim() === '') {
      setUsernameAvailable(null);
      setUsernameError('');
      setCheckingUsername(false);
      return;
    }

    // Don't check if it's the same as current username
    if (username.toLowerCase() === user.username?.toLowerCase()) {
      setUsernameAvailable(true);
      setUsernameError('');
      setCheckingUsername(false);
      return;
    }

    // Don't check if username is too short
    if (username.trim().length < 3) {
      setUsernameAvailable(null);
      setUsernameError('');
      setCheckingUsername(false);
      return;
    }

    setCheckingUsername(true);
    setUsernameError('');

    try {
      // Validate username with allowlist before request
      if (!/^[a-zA-Z0-9_-]{3,32}$/.test(username)) {
        setUsernameAvailable(false);
        setUsernameError('Invalid username format');
        return;
      }
      const response = await apiGet(`/api/users/check-username/${encodeURIComponent(username)}`);
      const data = await response.json();
      if (response.ok) {
        setUsernameAvailable(data.available);
        if (!data.available) {
          setUsernameError(data.message || 'Username is already taken');
        }
      } else {
        setUsernameAvailable(false);
        setUsernameError(data.message || 'Error checking username');
      }
    } catch (error) {
      setUsernameAvailable(false);
      setUsernameError('Error checking username availability');
    } finally {
      setCheckingUsername(false);
    }
  };

  // Debounced username check
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (nameInput && nameInput.trim() !== '') {
        checkUsernameAvailability(nameInput.trim());
      } else {
        setUsernameAvailable(null);
        setUsernameError('');
        setCheckingUsername(false);
      }
    }, 300); // Reduced delay for faster response

    return () => clearTimeout(timeoutId);
  }, [nameInput]);

  // Fetch user's products from backend
  const fetchUserProducts = async () => {
    setLoading(true);
    // setError('');
    try {
      let userId;
      let token = localStorage.getItem('token');
      if (isOwner) {
        userId = storeUser?.id;
      } else {
        userId = user?._id || user?.id;
        token = undefined; // public profile, don't send token
      }
      if (!userId) {
        // setError('User not found');
        setLoading(false);
        return;
      }
      let response;
      if (isOwner) {
        response = await apiGet(`/api/products/user/${userId}`);
      } else {
        response = await apiGet(`/api/products/public/user/${userId}`);
      }
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch products');
      }
      const data = await response.json();
      setProducts(data);
    } catch (err) {
      // setError(err.message || 'Failed to fetch products. Please try again later.');
    } finally {
      setLoading(false);
      setProductsFetched(true);
    }
  };

  // Simple sessionStorage cache helpers
  const getCache = (key) => {
    try {
      const raw = sessionStorage.getItem(key);
      const time = Number(sessionStorage.getItem(`${key}_time`));
      if (!raw || !time) return null;
      if (Date.now() - time > CACHE_TTL_MS) return null;
      const data = JSON.parse(raw);
      if (!Array.isArray(data) || data.length === 0) return null;
      return data;
    } catch (_) {
      return null;
    }
  };

  const setCache = (key, data) => {
    try {
      sessionStorage.setItem(key, JSON.stringify(data));
      sessionStorage.setItem(`${key}_time`, String(Date.now()));
    } catch (_) {}
  };

  const clearCache = (key) => {
    try {
      sessionStorage.removeItem(key);
      sessionStorage.removeItem(`${key}_time`);
    } catch (_) {}
  };

  // Fetch user's purchases from backend (with cache and abort)
  const fetchUserPurchases = async () => {
    setLoadingPurchases(true);
    setErrorPurchases('');
    try {
      const uid = isOwner ? (storeUser?.id || storeUser?._id) : (user?._id || user?.id);
      const cacheKey = `profile_purchases_${uid || 'me'}`;
      const cached = getCache(cacheKey);
      if (cached) {
        setPurchases(cached);
        setLoadingPurchases(false);
        return;
      }

      if (purchasesControllerRef.current) {
        try { purchasesControllerRef.current.abort(); } catch (_) {}
      }
      purchasesControllerRef.current = new AbortController();
      const response = await apiGet('/api/orders/my', { signal: purchasesControllerRef.current.signal });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch purchases');
      }
      const data = await response.json();
      setPurchases(data);
      if (Array.isArray(data) && data.length > 0) setCache(cacheKey, data); else clearCache(cacheKey);
    } catch (err) {
      setErrorPurchases(err.message || 'Failed to fetch purchases. Please try again later.');
      setPurchases([]);
    } finally {
      setLoadingPurchases(false);
    }
  };

  // Fetch user's sales from backend (with cache and abort)
  const fetchUserSales = async () => {
    setLoadingSales(true);
    setErrorSales('');
    try {
      const uid = isOwner ? (storeUser?.id || storeUser?._id) : (user?._id || user?.id);
      const cacheKey = `profile_sales_${uid || 'me'}`;
      const cached = getCache(cacheKey);
      if (cached) {
        setSales(cached);
        setLoadingSales(false);
        return;
      }

      if (salesControllerRef.current) {
        try { salesControllerRef.current.abort(); } catch (_) {}
      }
      salesControllerRef.current = new AbortController();
      const response = await apiGet('/api/orders/sales', { signal: salesControllerRef.current.signal });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch sales');
      }
      const data = await response.json();
      setSales(data);
      if (Array.isArray(data) && data.length > 0) setCache(cacheKey, data); else clearCache(cacheKey);
    } catch (err) {
      setErrorSales(err.message || 'Failed to fetch sales. Please try again later.');
      setSales([]);
    } finally {
      setLoadingSales(false);
    }
  };

  // For public view (when viewing someone else's profile)
  const lastPublicFetchRef = useRef({ key: null, ts: 0 });
  useEffect(() => {
    if (username && (!storeUser || username !== storeUser.username)) {
      const fetchKey = `public:${username}`;
      const now = Date.now();
      if (lastPublicFetchRef.current.key === fetchKey && (now - lastPublicFetchRef.current.ts) < 5000) {
        return;
      }
      lastPublicFetchRef.current = { key: fetchKey, ts: now };
      setLoading(true);
      // Fetch user data (now includes firebaseUid)
      apiGet(`/api/users/public/${username}`)
        .then(res => {
          if (!res.ok) throw new Error('User not found');
          return res.json();
        })
        .then(data => {
          setUser(data);
          setAvatar(data.avatar || generateModernAvatar(data.username));
          setCover(data.cover || '/images/default-cover.svg');
          setLoading(false);
        })
        .catch(err => {
          // setError(err.message);
          setLoading(false);
        });
    }
  }, [username]);

  // For owner view (when viewing your own profile)
  useEffect(() => {
    if ((!username || (storeUser && username === storeUser.username)) && storeUser) {
      const fetchKey = `owner:${storeUser.username}`;
      const now = Date.now();
      if (lastPublicFetchRef.current.key === fetchKey && (now - lastPublicFetchRef.current.ts) < 5000) {
        return;
      }
      lastPublicFetchRef.current = { key: fetchKey, ts: now };
      // Fetch latest user data to ensure banned status is up-to-date
              apiGet(`/api/users/public/${storeUser.username}`)
        .then(res => {
          if (!res.ok) throw new Error('User not found');
          return res.json();
        })
        .then(data => {
          // Always update user data to ensure verification status is current
          // Merge in transactions/totalAmount from auth if missing or stale
          setUser(data);
          // In parallel, refresh from /api/auth/me for most up-to-date counters
          apiGet('/api/auth/me')
            .then(r => r.ok ? r.json() : null)
            .then(me => {
              if (me && (typeof me.transactions !== 'undefined' || typeof me.totalAmount !== 'undefined')) {
                setUser(prev => ({ ...prev, 
                  transactions: typeof me.transactions !== 'undefined' ? me.transactions : prev?.transactions,
                  totalAmount: typeof me.totalAmount !== 'undefined' ? me.totalAmount : prev?.totalAmount,
                }));
              }
            })
            .catch(() => {});
          setAvatar(data.avatar || '/images/default-avatar.png');
          setCover(data.cover || '/images/default-cover.svg');
        })
        .catch(err => {
          safeReportError('profile_owner_fetch_failed');
          // Fallback to storeUser if API fails
          if (!user || user.id !== storeUser.id) {
            setUser(storeUser);
            setAvatar(storeUser?.avatar || '/images/default-avatar.png');
            setCover(storeUser?.cover || '/images/default-cover.svg');
          }
        });
    }
  }, [storeUser, username, user]);

  const userId = isOwner ? storeUser?.id : user?._id || user?.id;
  const firebaseUid = isOwner ? storeUser?.firebaseUid : user?.firebaseUid;

  // Fetch user reviews (seller or buyer based on context)
  const fetchUserReviews = async (userId, firebaseUid) => {
    if (!userId) return;
    try {
      setReviewsLoading(true);
      const sellerId = firebaseUid || userId;
      const buyerId = firebaseUid || userId;
      // First try seller reviews
      let response = await apiGet(`/api/reviews/seller/${sellerId}?limit=20`);
      let data;
      if (response.ok) {
        data = await response.json();
      } else {
        data = { reviews: [], stats: { averageRating: 0, totalReviews: 0 } };
      }
      // If no seller reviews, also try buyer reviews so buyer profiles show ratings
      if ((!data.reviews || data.reviews.length === 0)) {
        const resp2 = await apiGet(`/api/reviews/buyer/${buyerId}?limit=20`);
        if (resp2.ok) {
          const data2 = await resp2.json();
          data = data2;
        }
      }
      if (response.ok) {
        setReviews(data.reviews || []);
        setReviewStats(data.stats || { averageRating: 0, totalReviews: 0 });
      } else {
      }
    } catch (error) {
      safeReportError('user_reviews_load_failed', { userId, firebaseUid });
    } finally {
      setReviewsLoading(false);
    }
  };

  useEffect(() => {
    if (userId && (tab === 'listings' || tab === 'summary')) {
      fetchUserProducts();
    }
  }, [userId, tab]);

  // Prefetch reviews/stats on initial load and whenever profile user changes
  useEffect(() => {
    if (userId) {
      fetchUserReviews(userId, firebaseUid);
    }
    // eslint-disable-next-line
  }, [userId, firebaseUid]);

  useEffect(() => {
    if (tab === 'purchases') {
      fetchUserPurchases();
    }
    // eslint-disable-next-line
  }, [tab]);

  // Fetch sales when My Sales tab is active
  useEffect(() => {
    if (tab === 'sales') {
      fetchUserSales();
    }
    // eslint-disable-next-line
  }, [tab]);

  // Fetch reviews when Reviews tab is active
  useEffect(() => {
    if (tab === 'reviews' && userId) {
      fetchUserReviews(userId, firebaseUid);
    }
    // eslint-disable-next-line
  }, [tab, userId, firebaseUid]);

  // Abort in-flight requests on unmount or tab/user switch
  useEffect(() => {
    return () => {
      try { purchasesControllerRef.current?.abort(); } catch (_) {}
      try { salesControllerRef.current?.abort(); } catch (_) {}
    };
  }, []);

  // Summary metrics derived from current data
  const summaryMetrics = useMemo(() => {
    const listingsActive = Array.isArray(products) ? products.filter(p => (p.status || 'active').toLowerCase() === 'active').length : 0;
    const listingsPending = Array.isArray(products) ? products.filter(p => (p.status || '').toLowerCase() === 'pending').length : 0;
    const listingsSold = Array.isArray(products) ? products.filter(p => (p.status || '').toLowerCase() === 'sold').length : 0;
    const salesCount = Array.isArray(sales) ? sales.length : 0;
    const purchasesCount = Array.isArray(purchases) ? purchases.length : 0;
    const salesTotal = Array.isArray(sales) ? sales.reduce((sum, o) => sum + (Number(o.total) || Number(o.amount) || Number(o.price) || 0), 0) : 0;
    const purchasesTotal = Array.isArray(purchases) ? purchases.reduce((sum, o) => sum + (Number(o.total) || Number(o.amount) || Number(o.price) || 0), 0) : 0;
    return { listingsActive, listingsPending, listingsSold, salesCount, purchasesCount, salesTotal, purchasesTotal };
  }, [products, sales, purchases]);

  return (
    <>
      <SEOHead
        title={seoSettings?.title || 'Profile - YTProud'}
        description={seoSettings?.description || 'Manage your YTProud profile, view orders, and account settings.'}
        keywords={seoSettings?.keywords || 'profile, account settings, user profile'}
        ogTitle={seoSettings?.ogTitle || 'Profile - YTProud'}
        ogDescription={seoSettings?.ogDescription || 'Manage your profile and settings'}
        ogImage={seoSettings?.ogImage || '/images/profile-og.jpg'}
        schemaEnabled={seoSettings?.schemaEnabled || false}
        schemaJsonLd={seoSettings?.customSchemaJsonLd}
      />
      <Header />
      
      {/* Modern Notification */}
      {showNotification && (
        <div className={`fixed top-4 right-4 z-50 max-w-sm w-full bg-white rounded-lg shadow-lg border-l-4 ${
          notificationType === 'error' ? 'border-red-500' : 'border-green-500'
        } p-4 transform transition-all duration-300 ease-in-out`}>
          <div className="flex items-start">
            <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center ${
              notificationType === 'error' ? 'bg-red-100' : 'bg-green-100'
            }`}>
              {notificationType === 'error' ? (
                <svg className="w-4 h-4 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              ) : (
                <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              )}
            </div>
            <div className="ml-3 w-0 flex-1">
              <p className={`text-sm font-medium ${
                notificationType === 'error' ? 'text-red-800' : 'text-green-800'
              }`}>
                {notificationMessage}
              </p>
            </div>
            <div className="ml-4 flex-shrink-0 flex">
              <button
                onClick={() => setShowNotification(false)}
                className={`inline-flex text-gray-400 hover:text-gray-600 focus:outline-none focus:text-gray-600 transition ease-in-out duration-150`}
              >
                <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      <div className={`min-h-screen flex flex-col items-center py-4 sm:py-6 md:py-8 px-4 sm:px-6 transition-colors duration-300 ${
        isDarkMode ? 'bg-[#0f0f0f]' : 'bg-gray-50'
      }`}>
        <div className={`w-full max-w-[1650px] mx-auto rounded-xl sm:rounded-2xl shadow-lg sm:shadow-2xl overflow-hidden transition-all duration-500 ${
          isDarkMode 
            ? 'bg-[#2a2a2a] shadow-2xl shadow-black/50 border border-gray-700/50' 
            : 'bg-white'
        }`}>
          {/* Cover Art Banner */}
          <div className="w-full h-[140px] sm:h-[160px] md:h-[180px] lg:h-[220px] bg-gray-200 relative"
            onMouseEnter={() => isOwner && !user.isBanned && setHoverCover(true)}
            onMouseLeave={() => isOwner && !user.isBanned && setHoverCover(false)}
          >
            <img src={cover} alt="Cover" className="w-full h-full object-cover rounded-t-2xl" />
            {/* Camera/upload icon */}
            {isOwner && !user.isBanned && (
              <label className={`absolute top-2 sm:top-4 right-2 sm:right-4 bg-black bg-opacity-70 text-white text-xs px-2 py-1 rounded flex items-center gap-1 cursor-pointer hover:bg-opacity-90 transition z-10 ${hoverCover ? '' : 'opacity-0 pointer-events-none'}`}>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 sm:w-5 sm:h-5"><path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5V7.5A2.25 2.25 0 015.25 5.25h2.086a2.25 2.25 0 001.591-.659l.828-.828A2.25 2.25 0 0111.75 3h.5a2.25 2.25 0 011.595.663l.828.828a2.25 2.25 0 001.591.659h2.086A2.25 2.25 0 0121 7.5v9a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 16.5z" /><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 10.5a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z" /></svg>
                <span className="hidden sm:inline">Change Cover</span>
                <input type="file" accept="image/*" className="hidden" onChange={handleCoverChange} />
              </label>
            )}
            <span className="absolute bottom-2 right-2 sm:right-4 bg-black bg-opacity-70 text-white text-xs px-2 py-1 rounded hidden sm:block">Recommended: 1400x180px</span>
          </div>
          {/* Main Content Row */}
          <div className="flex flex-col lg:flex-row">
            {/* Left Profile Card */}
            <div className={`w-full lg:w-[400px] p-4 sm:p-6 flex flex-col items-center relative border-b lg:border-b-0 lg:border-r pt-0 transition-colors duration-300 ${
              isDarkMode ? 'border-gray-700' : 'border-gray-100'
            }`}>
              {/* Avatar - overlap cover */}
              <div className="relative z-10 -mt-12 sm:-mt-16 mb-2 flex flex-col items-center"
                onMouseEnter={() => isOwner && !user.isBanned && setHoverAvatar(true)}
                onMouseLeave={() => isOwner && !user.isBanned && setHoverAvatar(false)}
              >
                <img src={avatar} alt="Avatar" className="w-[80px] h-[80px] sm:w-[100px] sm:h-[100px] rounded-full border-4 border-white shadow-lg object-cover" />
                {/* Camera icon for avatar */}
                {isOwner && !user.isBanned && (
                  <label className={`absolute bottom-1 sm:bottom-2 right-1 sm:right-2 bg-black bg-opacity-70 text-white p-1 rounded-full cursor-pointer transition ${hoverAvatar ? '' : 'opacity-0 pointer-events-none'}`} title="Change Profile Picture">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 sm:w-5 sm:h-5"><path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5V7.5A2.25 2.25 0 015.25 5.25h2.086a2.25 2.25 0 001.591-.659l.828-.828A2.25 2.25 0 0111.75 3h.5a2.25 2.25 0 011.595.663l.828.828a2.25 2.25 0 001.591.659h2.086A2.25 2.25 0 0121 7.5v9a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 16.5z" /><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 10.5a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z" /></svg>
                    <input type="file" accept="image/*" className="hidden" onChange={handleAvatarChange} />
                  </label>
                )}
                {isOwner && <span className="absolute bottom-1 sm:bottom-2 left-1 sm:left-2 w-4 h-4 sm:w-5 sm:h-5 bg-green-500 border-2 border-white rounded-full"></span>}
                {/* Online Status Indicator - Only show for public profiles */}
                {!isOwner && userForPresence && (
                  <motion.div
                    initial={{ scale: 0, rotate: -180 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ duration: 0.4, type: "spring", stiffness: 200 }}
                    className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full border-2 transition-all duration-300 ${
                      userOnline 
                        ? 'bg-green-500 border-white shadow-lg' 
                        : 'bg-gray-400 border-white'
                    }`}
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <motion.div
                      className={`w-full h-full rounded-full ${
                        userOnline ? 'bg-green-400' : 'bg-gray-300'
                      }`}
                      animate={userOnline ? {
                        scale: [1, 1.1, 1],
                        opacity: [0.8, 1, 0.8]
                      } : {}}
                      transition={userOnline ? {
                        duration: 2,
                        repeat: Infinity,
                        ease: "easeInOut"
                      } : {}}
                    />
                    {userOnline && (
                      <motion.div
                        className="absolute inset-0 rounded-full border-2 border-green-300"
                        animate={{
                          scale: [1, 1.3, 1],
                          opacity: [0.7, 0, 0.7]
                        }}
                        transition={{
                          duration: 3,
                          repeat: Infinity,
                          ease: "easeOut"
                        }}
                      />
                    )}
                  </motion.div>
                )}
              </div>
              <h3 className={`text-xl sm:text-2xl font-bold flex items-center gap-2 text-center transition-colors duration-300 ${
                isDarkMode ? 'text-white' : 'text-black'
              }`}>
                {user.username || 'User'}
                {user.isBanned && (
                  <span className="text-red-600 font-bold">(banned)</span>
                )}
                {isOwner && !user.isBanned && (
                  <button onClick={() => {
                    setShowEditName(true);
                    setNameInput(user.username || '');
                    setUsernameAvailable(null);
                    setUsernameError('');
                  }} className="ml-2 text-gray-500 hover:text-red-600" title="Edit Name">
                    <FaPencilAlt className="w-4 h-4 sm:w-5 sm:h-5" />
                  </button>
                )}
                {user.verified && <button onClick={() => setShowVerificationPopup(true)} className="inline hover:scale-110 transition-transform duration-200" title="Verified - Click for details"><svg width="18" height="18" className="sm:w-[22px] sm:h-[22px] inline" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="12" fill={isDarkMode ? "#6B7280" : "#2563eb"}/><path d="M7 13l3 3 7-7" stroke={isDarkMode ? "#1F2937" : "#ffffff"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg></button>}
              </h3>
              <div className={`font-semibold mb-2 text-center transition-colors duration-300 ${
                isDarkMode ? 'text-gray-300' : 'text-gray-500'
              }`}>Account details</div>
              <div className={`text-base sm:text-lg font-bold mb-1 text-center transition-colors duration-300 ${
                isDarkMode ? 'text-white' : 'text-black'
              }`}>
                {user.transactions || 0}
                <span className={`font-normal transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-500'
                }`}> Transactions totaling </span>
                <span className="text-base sm:text-lg font-bold">
                  ${user.totalAmount ? user.totalAmount.toLocaleString(undefined, { minimumFractionDigits: 2 }) : '0.00'}
                </span>
              </div>
              <div className={`mb-2 text-center text-sm sm:text-base transition-colors duration-300 ${
                isDarkMode ? 'text-gray-300' : 'text-gray-500'
              }`}>Member since: <span className={`font-bold transition-colors duration-300 ${
                isDarkMode ? 'text-white' : 'text-black'
              }`}>{formatDate(user.createdAt)}</span></div>
              <div className={`rounded-xl shadow p-3 sm:p-4 w-full text-center mt-2 transition-all duration-500 ${
                isDarkMode 
                  ? 'bg-[#1a1a1a] border border-gray-700/50 shadow-lg shadow-black/30' 
                  : 'bg-white'
              }`}>
                <div className={`text-base sm:text-lg font-semibold mb-1 transition-colors duration-300 ${
                  isDarkMode ? 'text-white' : 'text-black'
                }`}>Ratings</div>
                <div className="flex items-center justify-center gap-2 mb-1">
                  <span className={`text-xl sm:text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-black'}`}>{(reviewStats.averageRating || 0).toFixed(1)}</span>
                  <span className="text-yellow-400 text-lg sm:text-xl">{'★'.repeat(Math.round(reviewStats.averageRating || 0))}{'☆'.repeat(5 - Math.round(reviewStats.averageRating || 0))}</span>
                </div>
                <div className={`text-xs sm:text-sm transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-500'
                }`}>Based on <span className="text-red-600 font-bold underline cursor-pointer">{reviewStats.totalReviews || 0} review{(reviewStats.totalReviews || 0) !== 1 ? 's' : ''}</span></div>
              </div>
              {/* Edit Name Modal */}
              {isOwner && showEditName && (
                <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-40 p-4">
                         <div className={`rounded-xl shadow-lg p-4 sm:p-6 md:p-8 w-full max-w-xl relative transition-all duration-300 ${
                           isDarkMode 
                             ? 'bg-[#2a2a2a] shadow-2xl shadow-black/50 border border-gray-700/50' 
                             : 'bg-white'
                         }`}>
                           <h2 className={`text-xl sm:text-2xl font-bold mb-4 flex items-center gap-2 transition-colors duration-300 ${
                             isDarkMode ? 'text-white' : 'text-black'
                           }`}>
                      <span className="inline-block"><FaPencilAlt className="w-5 h-5" /></span> Edit Profile Name
                    </h2>
                           
                           {/* Username Input with Status */}
                           <div className="relative mb-4">
                    <input
                                className={`w-full border rounded p-3 text-base transition-all duration-300 pr-12 ${
                                  isDarkMode 
                                    ? 'bg-[#1a1a1a] border-gray-600 text-white placeholder-gray-400 focus:border-red-500 focus:ring-red-500' 
                                    : 'bg-white border-gray-300 text-black placeholder-gray-500 focus:border-red-500 focus:ring-red-500'
                                } ${
                                  usernameAvailable === true ? 'border-green-500 focus:border-green-500 focus:ring-green-500' :
                                  usernameAvailable === false ? 'border-red-500 focus:border-red-500 focus:ring-red-500' :
                                  checkingUsername ? 'border-yellow-500 focus:border-yellow-500 focus:ring-yellow-500' :
                                  nameInput && nameInput.trim().length > 0 && nameInput.trim().length < 3 ? 'border-gray-400' : ''
                                }`}
                      value={nameInput}
                      onChange={e => setNameInput(e.target.value)}
                      placeholder="Enter your profile name..."
                                disabled={savingUsername}
                              />
                             
                                                           {/* Status Icon */}
                              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                {checkingUsername ? (
                                  <div className="flex items-center justify-center w-6 h-6">
                                    <svg className="animate-spin h-5 w-5 text-yellow-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                  </div>
                                ) : usernameAvailable === true ? (
                                  <div className="flex items-center justify-center w-6 h-6">
                                    <svg className="h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                    </svg>
                                  </div>
                                ) : usernameAvailable === false ? (
                                  <div className="flex items-center justify-center w-6 h-6">
                                    <svg className="h-5 w-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                  </div>
                                ) : nameInput && nameInput.trim().length > 0 && nameInput.trim().length < 3 ? (
                                  <div className="flex items-center justify-center w-6 h-6">
                                    <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                  </div>
                                ) : null}
                              </div>
                           </div>

                                                       {/* Username Status Message */}
                            {checkingUsername && (
                              <div className={`mb-4 p-3 rounded-lg border transition-all duration-300 animate-pulse ${
                                isDarkMode 
                                  ? 'bg-yellow-900/20 border-yellow-700/50 text-yellow-300' 
                                  : 'bg-yellow-50 border-yellow-200 text-yellow-800'
                              }`}>
                                <div className="flex items-center gap-2">
                                  <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                  </svg>
                                  <span className="text-sm font-medium">Checking username availability...</span>
                                </div>
                              </div>
                            )}



                            {/* Show checking state even when input is too short */}
                            {nameInput && nameInput.trim().length > 0 && nameInput.trim().length < 3 && !checkingUsername && (
                              <div className={`mb-4 p-3 rounded-lg border transition-all duration-300 ${
                                isDarkMode 
                                  ? 'bg-gray-900/20 border-gray-700/50 text-gray-300' 
                                  : 'bg-gray-50 border-gray-200 text-gray-800'
                              }`}>
                                <div className="flex items-center gap-2">
                                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                  <span className="text-sm font-medium">Username must be at least 3 characters</span>
                                </div>
                              </div>
                            )}

                           {usernameAvailable === true && nameInput.trim() !== '' && (
                             <div className={`mb-4 p-3 rounded-lg border transition-all duration-300 ${
                               isDarkMode 
                                 ? 'bg-green-900/20 border-green-700/50 text-green-300' 
                                 : 'bg-green-50 border-green-200 text-green-800'
                             }`}>
                               <div className="flex items-center gap-2">
                                 <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                 </svg>
                                 <span className="text-sm font-medium">Username is available!</span>
                               </div>
                             </div>
                           )}

                           {usernameAvailable === false && usernameError && (
                             <div className={`mb-4 p-3 rounded-lg border transition-all duration-300 ${
                               isDarkMode 
                                 ? 'bg-red-900/20 border-red-700/50 text-red-300' 
                                 : 'bg-red-50 border-red-200 text-red-800'
                             }`}>
                               <div className="flex items-center gap-2">
                                 <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                 </svg>
                                 <span className="text-sm font-medium">{usernameError}</span>
                               </div>
                             </div>
                           )}

                    <div className="flex flex-col sm:flex-row justify-end gap-2">
                      <button
                               className={`px-4 py-2 rounded text-sm sm:text-base font-semibold transition-all duration-300 ${
                                 isDarkMode 
                                   ? 'bg-gray-700 text-gray-200 hover:bg-gray-600 border border-gray-600' 
                                   : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                               }`} 
                               onClick={() => {
                                 setShowEditName(false);
                                 setUsernameAvailable(null);
                                 setUsernameError('');
                                 setNameInput(user.username || '');
                               }}
                               disabled={savingUsername}
                             >
                               Cancel
                             </button>
                             <button
                               className={`px-4 py-2 rounded text-white font-semibold transition-all duration-300 text-sm sm:text-base ${
                                 usernameAvailable === true && !savingUsername
                                   ? 'bg-red-600 hover:bg-red-700'
                                   : 'bg-gray-400 cursor-not-allowed'
                               }`}
                               disabled={usernameAvailable !== true || savingUsername}
                        onClick={async () => {
                                 if (usernameAvailable !== true) return;
                                 
                                 setSavingUsername(true);
                                 try {
                                   const response = await apiPatch('/api/auth/profile', { username: nameInput.trim() });
                                   if (!response.ok) {
                                     const errorData = await response.json();
                                     throw new Error(errorData.message || 'Failed to update username');
                                   }
                            const updatedUser = await response.json();
                            setUser(updatedUser);
                                   setAuthUser(updatedUser);
                            localStorage.setItem('user', JSON.stringify(updatedUser));
                            setShowEditName(false);
                                   setUsernameAvailable(null);
                                   setUsernameError('');
                                   
                                   // Show success notification
                                   setNotificationMessage('Username updated successfully!');
                                   setNotificationType('success');
                                   setShowNotification(true);
                          } catch (err) {
                                   setUsernameError(err.message || 'Failed to update username');
                                   setNotificationMessage(err.message || 'Failed to update username');
                                   setNotificationType('error');
                                   setShowNotification(true);
                                 } finally {
                                   setSavingUsername(false);
                                 }
                               }}
                             >
                               {savingUsername ? (
                                 <span className="flex items-center justify-center">
                                   <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                     <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                     <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                   </svg>
                                   Saving...
                                 </span>
                               ) : (
                                 'Save'
                               )}
                             </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
            {/* Right Dashboard */}
            <div className="flex-1 p-4 sm:p-6 md:p-8 min-w-0">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                <div className={`text-xl sm:text-2xl font-bold transition-colors duration-300 ${
                  isDarkMode ? 'text-white' : 'text-gray-900'
                }`}>
                  Hello, {user.username || 'User'}! <span className="text-lg sm:text-xl">✌️</span>
                  {user.isBanned && (
                    <span className="text-red-600 font-bold">(banned)</span>
                  )}
                </div>
                {isOwner && !user.isBanned && (
                  <div className="flex flex-col sm:flex-row gap-2">
                    <button className="px-3 sm:px-4 py-2 rounded border border-gray-300 bg-white text-gray-700 font-semibold hover:bg-gray-100 transition text-sm sm:text-base">See Public View</button>
                    <button className="px-3 sm:px-4 py-2 rounded bg-red-600 text-white font-semibold hover:bg-red-700 transition text-sm sm:text-base">Profile Settings</button>
                  </div>
                )}
              </div>
              {/* Tabs */}
              <div className={`flex flex-wrap gap-4 sm:gap-6 md:gap-8 border-b mb-6 sm:mb-8 text-base sm:text-lg font-semibold overflow-x-auto transition-colors duration-300 ${
                isDarkMode ? 'border-gray-700' : 'border-gray-200'
              }`}>
                <button className={tab === 'summary' ? (isDarkMode ? 'text-blue-400 border-b-2 border-blue-400 pb-2' : 'text-red-600 border-b-2 border-red-600 pb-2') : isDarkMode ? 'text-gray-300 pb-2 hover:text-white' : 'text-gray-600 pb-2'} onClick={() => setTab('summary')}>Summary</button>
                <button className={tab === 'listings' ? (isDarkMode ? 'text-blue-400 border-b-2 border-blue-400 pb-2' : 'text-red-600 border-b-2 border-red-600 pb-2') : isDarkMode ? 'text-gray-300 pb-2 hover:text-white' : 'text-gray-600 pb-2'} onClick={() => setTab('listings')}>My Listings</button>
                <button className={tab === 'reviews' ? (isDarkMode ? 'text-blue-400 border-b-2 border-blue-400 pb-2' : 'text-red-600 border-b-2 border-red-600 pb-2') : isDarkMode ? 'text-gray-300 pb-2 hover:text-white' : 'text-gray-600 pb-2'} onClick={() => setTab('reviews')}>Reviews</button>
                {isOwner && <button className={tab === 'sales' ? (isDarkMode ? 'text-blue-400 border-b-2 border-blue-400 pb-2' : 'text-red-600 border-b-2 border-red-600 pb-2') : isDarkMode ? 'text-gray-300 pb-2 hover:text-white' : 'text-gray-600 pb-2'} onClick={() => setTab('sales')}>My Sales</button>}
                {isOwner && <button className={tab === 'purchases' ? (isDarkMode ? 'text-blue-400 border-b-2 border-blue-400 pb-2' : 'text-red-600 border-b-2 border-red-600 pb-2') : isDarkMode ? 'text-gray-300 pb-2 hover:text-white' : 'text-gray-600 pb-2'} onClick={() => setTab('purchases')}>My Purchases</button>}
              </div>
              {/* Banned User Message - Modern Design */}
              {user.isBanned && (
                <div className={`mb-6 relative overflow-hidden rounded-2xl border backdrop-blur-sm ${
                  isDarkMode 
                    ? 'bg-gradient-to-br from-red-900/30 via-red-800/20 to-red-900/30 border-red-700/50 shadow-2xl shadow-red-900/20' 
                    : 'bg-gradient-to-br from-red-50 via-red-100/50 to-red-50 border-red-200 shadow-xl shadow-red-100/50'
                }`}>
                  {/* Animated background pattern */}
                  <div className="absolute inset-0 opacity-10">
                    <div className={`absolute inset-0 ${
                      isDarkMode ? 'bg-red-500' : 'bg-red-400'
                    }`} style={{
                      backgroundImage: `radial-gradient(circle at 25% 25%, transparent 20%, rgba(255,255,255,0.1) 21%, rgba(255,255,255,0.1) 34%, transparent 35%), radial-gradient(circle at 75% 75%, transparent 20%, rgba(255,255,255,0.1) 21%, rgba(255,255,255,0.1) 34%, transparent 35%)`,
                      backgroundSize: '30px 30px'
                    }}></div>
                  </div>
                  
                  <div className="relative p-6">
                    <div className="flex items-start space-x-4">
                      {/* Modern Icon */}
                      <div className={`flex-shrink-0 w-12 h-12 rounded-2xl flex items-center justify-center ${
                        isDarkMode 
                          ? 'bg-red-800/50 border border-red-700/50' 
                          : 'bg-red-200/80 border border-red-300/50'
                      }`}>
                        <svg className="w-6 h-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        {/* Header with badge */}
                        <div className="flex items-center space-x-3 mb-3">
                          <h3 className={`text-lg font-bold ${isDarkMode ? 'text-red-100' : 'text-red-800'}`}>
                            Account Suspended
                          </h3>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            isDarkMode 
                              ? 'bg-red-800/50 text-red-200 border border-red-700/50' 
                              : 'bg-red-200 text-red-800 border border-red-300'
                          }`}>
                            <div className={`w-1.5 h-1.5 rounded-full mr-1.5 ${
                              isDarkMode ? 'bg-red-400' : 'bg-red-500'
                            }`}></div>
                            RESTRICTED
                          </span>
                        </div>
                        
                        {/* Content */}
                        <div className={`space-y-3 ${isDarkMode ? 'text-red-200' : 'text-red-700'}`}>
                          {isOwner ? (
                            <>
                              <p className="text-sm font-medium">Your account has been suspended. You cannot:</p>
                              <div className="space-y-2">
                                {[
                                  'Edit your profile information',
                                  'Change your profile or cover images',
                                  'Make new purchases'
                                ].map((restriction, index) => (
                                  <div key={index} className="flex items-center space-x-2">
                                    <div className={`w-1.5 h-1.5 rounded-full ${
                                      isDarkMode ? 'bg-red-400' : 'bg-red-500'
                                    }`}></div>
                                    <span className="text-sm">{restriction}</span>
                                  </div>
                                ))}
                              </div>
                            </>
                          ) : (
                            <p className="text-sm">This account has been suspended by our administrators.</p>
                          )}
                          
                          {user.banReason && (
                            <div className={`mt-4 p-3 rounded-xl ${
                              isDarkMode 
                                ? 'bg-red-800/30 border border-red-700/30' 
                                : 'bg-red-100/80 border border-red-200/50'
                            }`}>
                              <div className="flex items-center space-x-2">
                                <svg className="w-4 h-4 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <span className={`text-sm font-medium ${isDarkMode ? 'text-red-200' : 'text-red-800'}`}>
                                  Reason: {user.banReason}
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Tab Content */}
              {tab === 'summary' && (
                <div>
                  <div className="mb-8">
                    <div className="flex items-center gap-2 sm:gap-3 mb-2 text-lg sm:text-xl font-bold">
                      <FaUser className="w-5 h-5 sm:w-6 sm:h-6 text-red-600" />
                      Profile Description
                      {isOwner && !user.isBanned && (
                        <button onClick={() => setShowEditDesc(true)} className="ml-2 text-gray-500 hover:text-red-600" title="Edit Description">
                          <FaPencilAlt className="w-4 h-4 sm:w-5 sm:h-5" />
                        </button>
                      )}
                    </div>
                    <div className={`text-base sm:text-lg mb-4 sm:mb-6 transition-colors duration-300 ${
                      isDarkMode ? 'text-white' : 'text-gray-700'
                    }`}>
                      {user.description || 'Welcome!'}
                    </div>
                    {/* Edit Description Modal */}
                    {isOwner && showEditDesc && (
                      <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-40 p-4">
                        <div className={`rounded-xl shadow-lg p-4 sm:p-6 md:p-8 w-full max-w-xl relative transition-all duration-300 ${
                          isDarkMode 
                            ? 'bg-[#2a2a2a] shadow-2xl shadow-black/50 border border-gray-700/50' 
                            : 'bg-white'
                        }`}>
                          <h2 className={`text-xl sm:text-2xl font-bold mb-4 flex items-center gap-2 transition-colors duration-300 ${
                            isDarkMode ? 'text-white' : 'text-black'
                          }`}>
                            <span className="inline-block"><FaPencilAlt className="w-5 h-5" /></span> Edit Profile Description
                          </h2>
                          <textarea
                            className={`w-full border rounded p-3 mb-4 min-h-[120px] text-base transition-all duration-300 resize-none ${
                              isDarkMode 
                                ? 'bg-[#1a1a1a] border-gray-600 text-white placeholder-gray-400 focus:border-red-500 focus:ring-red-500' 
                                : 'bg-white border-gray-300 text-black placeholder-gray-500 focus:border-red-500 focus:ring-red-500'
                            }`}
                            value={descInput}
                            onChange={e => setDescInput(e.target.value)}
                            placeholder="Enter your profile description..."
                          />
                          <div className="flex flex-col sm:flex-row justify-end gap-2">
                            <button 
                              className={`px-4 py-2 rounded text-sm sm:text-base font-semibold transition-all duration-300 ${
                                isDarkMode 
                                  ? 'bg-gray-700 text-gray-200 hover:bg-gray-600 border border-gray-600' 
                                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                              }`} 
                              onClick={() => setShowEditDesc(false)}
                            >
                              Cancel
                            </button>
                            <button
                              className="px-4 py-2 rounded bg-red-600 text-white font-semibold hover:bg-red-700 transition disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
                              disabled={savingDescription}
                              onClick={async () => {
                                setSavingDescription(true);
                                try {
                                  const response = await apiPatch('/api/auth/profile', { description: descInput });
                                  if (!response.ok) throw new Error('Failed to update description');
                                  const updatedUser = await response.json();
                                  setUser(updatedUser);
                                  setAuthUser(updatedUser); // Update auth store
                                  localStorage.setItem('user', JSON.stringify(updatedUser));
                                  setShowEditDesc(false);
                                } catch (err) {
                                  alert('Failed to update description');
                                } finally {
                                  setSavingDescription(false);
                                }
                              }}
                            >
                              {savingDescription ? (
                                <span className="flex items-center justify-center">
                                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                  </svg>
                                  Saving...
                                </span>
                              ) : (
                                'Save'
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  {/* Featured Listings Section */}
                  <div className="mb-6 sm:mb-8">
                    <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6 text-lg sm:text-xl font-bold">
                      <FaShoppingCart className="w-5 h-5 sm:w-6 sm:h-6 text-red-600" />
                      Featured Listings
                    </div>
                    {!productsFetched ? (
                      <div className="w-full">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 sm:gap-3 lg:gap-6">
                          {[...Array(4)].map((_, idx) => (
                            <ProductCardSkeleton key={idx} />
                          ))}
                        </div>
                      </div>
                    ) : products.filter(p => p.status === 'published').length > 0 ? (
                      <div className="w-full">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 sm:gap-3 lg:gap-6">
                          {products.filter(p => p.status === 'published').slice(-4).reverse().map((item, idx) => (
                            <ProductCard key={item._id || idx} product={item} featured={true} />
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className={`flex flex-col items-center justify-center py-8 sm:py-16 rounded-xl shadow-none border-none mx-auto max-w-lg mt-4 sm:mt-8 transition-all duration-300 ${
                        isDarkMode 
                          ? 'bg-[#2a2a2a] shadow-2xl shadow-black/50 border border-gray-700/50' 
                          : 'bg-white'
                      }`}>
                        {/* Shopping bags SVG icon */}
                        <svg width="60" height="60" className="sm:w-[80px] sm:h-[80px] mb-4" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <rect x="14" y="20" width="24" height="28" rx="3" fill="#F87171" stroke="#111" strokeWidth="2"/>
                          <rect x="28" y="28" width="18" height="20" rx="2.5" fill="#FBBF24" stroke="#111" strokeWidth="2"/>
                          <path d="M20 20V16a8 8 0 0116 0v4" stroke="#111" strokeWidth="2"/>
                          <circle cx="22" cy="32" r="2" fill="#fff" stroke="#111" strokeWidth="1.5"/>
                          <circle cx="34" cy="32" r="2" fill="#fff" stroke="#111" strokeWidth="1.5"/>
                        </svg>
                        <div className={`text-lg sm:text-xl font-bold mb-1 text-center transition-colors duration-300 ${
                          isDarkMode ? 'text-white' : 'text-gray-900'
                        }`}>
                          No active or sold listings to display.
                        </div>
                        <div className={`text-sm sm:text-base text-center transition-colors duration-300 ${
                          isDarkMode ? 'text-gray-400' : 'text-gray-500'
                        }`}>
                          Product listings will be displayed here when available.
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
              {tab === 'listings' && (
                <div>
                  <div className="mb-8">
                    <div className="mb-4 sm:mb-6">
                      <div className={`inline-flex flex-wrap gap-2 sm:gap-3 rounded-xl px-3 sm:px-4 py-2 items-center shadow-sm transition-all duration-300 ${
                        isDarkMode 
                          ? 'bg-gradient-to-br from-[#2a2a2a] via-[#1a1a1a] to-[#2a2a2a] border border-gray-700/50 shadow-lg shadow-black/30' 
                          : 'bg-white border border-gray-200'
                      }`}>
                        <button
                          className={`px-4 sm:px-6 py-2.5 rounded-lg font-semibold text-sm sm:text-base transition-all duration-200 ${
                            listingStatus === 'active' 
                              ? 'bg-red-600 text-white shadow-md' 
                              : isDarkMode 
                                ? 'bg-gray-700 text-gray-200 hover:bg-gray-600 border border-gray-600' 
                                : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200'
                          }`}
                          onClick={() => setListingStatus('active')}
                        >
                          Active ({products.filter(p => (isOwner && (p.status === 'published' || p.status === 'active') && p.status !== 'sold' && p.stockStatus !== 'outofstock') || (!isOwner && p.status === 'published' && p.status !== 'sold' && p.stockStatus !== 'outofstock')).length})
                        </button>
                        {isOwner && (
                          <button
                            className={`px-4 sm:px-6 py-2.5 rounded-lg font-semibold text-sm sm:text-base transition-all duration-200 ${
                              listingStatus === 'pending' 
                                ? 'bg-red-600 text-white shadow-md' 
                                : isDarkMode 
                                  ? 'bg-gray-700 text-gray-200 hover:bg-gray-600 border border-gray-600' 
                                  : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200'
                            }`}
                            onClick={() => setListingStatus('pending')}
                          >
                            Pending ({products.filter(p => p.status === 'pending').length})
                          </button>
                        )}
                        {isOwner && (
                          <button
                            className={`px-4 sm:px-6 py-2.5 rounded-lg font-semibold text-sm sm:text-base transition-all duration-200 ${
                              listingStatus === 'reject' 
                                ? 'bg-red-600 text-white shadow-md' 
                                : isDarkMode 
                                  ? 'bg-gray-700 text-gray-200 hover:bg-gray-600 border border-gray-600' 
                                  : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200'
                            }`}
                            onClick={() => setListingStatus('reject')}
                          >
                            Reject ({products.filter(p => p.status === 'reject' || p.status === 'rejected' || p.status === 'riject').length})
                          </button>
                        )}
                        <button
                          className={`px-4 sm:px-6 py-2.5 rounded-lg font-semibold text-sm sm:text-base transition-all duration-200 ${
                            listingStatus === 'sold' 
                              ? 'bg-red-600 text-white shadow-md' 
                              : isDarkMode 
                                ? 'bg-gray-700 text-gray-200 hover:bg-gray-600 border border-gray-600' 
                                : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200'
                          }`}
                          onClick={() => setListingStatus('sold')}
                        >
                          Sold ({products.filter(p => p.status === 'sold' || p.stockStatus === 'outofstock').length})
                        </button>
                      </div>
                    </div>
                    {loading ? (
                      <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
                        {[...Array(6)].map((_, idx) => (
                          <ProductCardSkeleton key={idx} />
                        ))}
                      </div>
                    ) : !loading && (
                      products.length === 0 ||
                      (listingStatus === 'active' && products.filter(p => (isOwner ? (['published', 'active'].includes(p.status) && p.status !== 'sold' && p.stockStatus !== 'outofstock') : (p.status === 'published' && p.status !== 'sold' && p.stockStatus !== 'outofstock'))).length === 0) ||
                      (listingStatus === 'pending' && products.filter(p => (p.status === 'pending' || p.status === 'pendings')).length === 0) ||
                      (listingStatus === 'reject' && products.filter(p => (p.status === 'reject' || p.status === 'rejected' || p.status === 'riject')).length === 0) ||
                      (listingStatus === 'sold' && products.filter(p => (isOwner ? (p.status === 'sold' || p.stockStatus === 'outofstock') : (p.status === 'sold' || p.stockStatus === 'outofstock'))).length === 0)
                    ) ? (
                      <div className={`flex flex-col items-center justify-center py-16 rounded-xl shadow-none border-none mx-auto max-w-lg mt-8 transition-all duration-300 ${
                        isDarkMode 
                          ? 'bg-[#2a2a2a] shadow-2xl shadow-black/50 border border-gray-700/50' 
                          : 'bg-white'
                      }`}>
                        {/* Shopping bags SVG icon */}
                        <svg width="80" height="80" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="mb-4">
                          <rect x="14" y="20" width="24" height="28" rx="3" fill="#F87171" stroke="#111" strokeWidth="2"/>
                          <rect x="28" y="28" width="18" height="20" rx="2.5" fill="#FBBF24" stroke="#111" strokeWidth="2"/>
                          <path d="M20 20V16a8 8 0 0116 0v4" stroke="#111" strokeWidth="2"/>
                          <circle cx="22" cy="32" r="2" fill="#fff" stroke="#111" strokeWidth="1.5"/>
                          <circle cx="34" cy="32" r="2" fill="#fff" stroke="#111" strokeWidth="1.5"/>
                        </svg>
                        <div className={`text-xl font-bold mb-1 text-center transition-colors duration-300 ${
                          isDarkMode ? 'text-white' : 'text-gray-900'
                        }`}>
                          {listingStatus === 'active' && 'No active listings to display.'}
                          {listingStatus === 'pending' && 'No pending listings to display.'}
                          {listingStatus === 'reject' && 'No rejected listings to display.'}
                          {listingStatus === 'sold' && 'No sold listings to display.'}
                        </div>
                        <div className={`text-base text-center transition-colors duration-300 ${
                          isDarkMode ? 'text-gray-400' : 'text-gray-500'
                        }`}>
                          Product listings will be displayed here when available.
                        </div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
                        {products.filter(p =>
                          (listingStatus === 'active' && ((isOwner && (p.status === 'published' || p.status === 'active') && p.status !== 'sold' && p.stockStatus !== 'outofstock') || (!isOwner && p.status === 'published' && p.status !== 'sold' && p.stockStatus !== 'outofstock')))
                          || (listingStatus === 'pending' && (p.status === 'pending' || p.status === 'pendings'))
                          || (listingStatus === 'reject' && (p.status === 'reject' || p.status === 'rejected' || p.status === 'riject'))
                          || (listingStatus === 'sold' && ((isOwner && (p.status === 'sold' || p.stockStatus === 'outofstock')) || (!isOwner && (p.status === 'sold' || p.stockStatus === 'outofstock'))))
                        ).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)) // Sort by latest upload first
                        .slice(0, listingsToShow).map((product, idx) => (
                          <ProductCard key={product._id || idx} product={product} />
                        ))}
                      </div>
                    )}
                    {(!loading && products.filter(p =>
                      (listingStatus === 'active' && ((isOwner && (p.status === 'published' || p.status === 'active') && p.status !== 'sold' && p.stockStatus !== 'outofstock') || (!isOwner && p.status === 'published' && p.status !== 'sold' && p.stockStatus !== 'outofstock')))
                      || (listingStatus === 'pending' && (p.status === 'pending' || p.status === 'pendings'))
                      || (listingStatus === 'reject' && (p.status === 'reject' || p.status === 'rejected' || p.status === 'riject'))
                      || (listingStatus === 'sold' && ((isOwner && (p.status === 'sold' || p.stockStatus === 'outofstock')) || (!isOwner && (p.status === 'sold' || p.stockStatus === 'outofstock'))))
                    ).length > listingsToShow) && (
                      <div className="flex justify-center mt-4 sm:mt-6">
                        <button
                          className="px-4 sm:px-6 py-2 rounded-full bg-red-600 text-white font-semibold hover:bg-red-700 transition text-sm sm:text-base"
                          onClick={() => setListingsToShow(listingsToShow + 6)}
                        >
                          Load More
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
              {tab === 'reviews' && (
                <div>
                  <div className="mb-6 sm:mb-8">
                    <div className="text-xl sm:text-2xl font-bold mb-4">Reviews</div>
                    {/* Review Summary Card */}
                    <div className={`rounded-2xl shadow p-4 sm:p-6 md:p-8 flex flex-col md:flex-row items-center md:items-start gap-4 sm:gap-6 md:gap-8 mb-6 sm:mb-8 transition-all duration-500 ${
                      isDarkMode 
                        ? 'bg-[#2a2a2a] shadow-2xl shadow-black/50 border border-gray-700/50' 
                        : 'bg-white'
                    }`}>
                                              <div className="flex flex-col items-center justify-center w-full md:w-1/4">
                        <div className={`text-4xl sm:text-5xl font-bold mb-2 transition-colors duration-300 ${
                          isDarkMode ? 'text-white' : 'text-gray-900'
                        }`}>
                          {reviews.length > 0 
                            ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
                            : '0.0'
                          }
                        </div>
                          <div className="flex gap-1 mb-2">
                            {[...Array(5)].map((_, i) => (
                              <FaStar 
                                key={i} 
                                className={`text-lg sm:text-xl ${
                                  i < Math.round(reviews.length > 0 ? reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length : 0)
                                    ? 'text-yellow-400' 
                                    : isDarkMode ? 'text-gray-600' : 'text-gray-300'
                                }`} 
                              />
                            ))}
                          </div>
                        <div className={`text-xs sm:text-sm transition-colors duration-300 ${
                          isDarkMode ? 'text-gray-400' : 'text-gray-500'
                        }`}>based on {reviews.length} review{reviews.length !== 1 ? 's' : ''}</div>
                        </div>
                      <div className="flex-1 w-full">
                        {[5,4,3,2,1].map(star => {
                          const count = reviews.filter(r => r.rating === star).length;
                          const percentage = reviews.length > 0 ? (count / reviews.length) * 100 : 0;
                          return (
                            <div key={star} className="flex items-center gap-2 mb-1">
                              <span className={`text-sm w-6 transition-colors duration-300 ${
                                isDarkMode ? 'text-gray-400' : 'text-gray-500'
                              }`}>{star} <FaStar className={`inline text-xs transition-colors duration-300 ${
                                isDarkMode ? 'text-gray-500' : 'text-gray-400'
                              }`} /></span>
                              <div className={`flex-1 h-2 rounded overflow-hidden transition-colors duration-300 ${
                                isDarkMode ? 'bg-gray-700' : 'bg-gray-100'
                              }`}>
                                <div className={`h-2 rounded bg-red-500`} style={{width: `${percentage}%`}}></div>
                              </div>
                              <span className={`text-sm w-4 text-right transition-colors duration-300 ${
                                isDarkMode ? 'text-gray-400' : 'text-gray-500'
                              }`}>{count}</span>
                            </div>
                          );
                        })}
                                </div>
                              </div>
                    {/* Review Cards */}
                    {reviewsLoading ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                        {[...Array(6)].map((_, i) => (
                          <div key={i} className={`rounded-2xl p-5 shadow-lg animate-pulse ${isDarkMode ? 'bg-[#1a1a1a] border border-gray-700' : 'bg-white border border-gray-200'}`}>
                            <div className="flex items-center gap-3 mb-3">
                              <div className={`w-10 h-10 rounded-full ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}></div>
                              <div className="flex-1">
                                <div className={`h-4 w-24 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'} mb-2`}></div>
                                <div className={`h-3 w-16 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}></div>
                              </div>
                            </div>
                            <div className={`h-16 w-full rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'} mb-2`}></div>
                            <div className={`h-3 w-20 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}></div>
                          </div>
                        ))}
                      </div>
                    ) : reviews.length > 0 ? (
                      <ReviewGrid
                        reviews={reviews}
                        initialCount={9}
                        step={9}
                      />
                    ) : (
                      <div className={`text-center py-12 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        <p className="text-lg">No reviews yet.</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
              {tab === 'sales' && (
                <div>
                  <div className="mb-8">
                    <div className="text-2xl font-bold mb-4">My Sales (Total Items Sold: {sales.length})</div>
                    {/* Status Filter Bar */}
                    <div className="mb-8">
                      <div className={`inline-flex flex-wrap gap-2 sm:gap-3 rounded-xl px-3 sm:px-4 py-2 items-center shadow-sm transition-all duration-300 ${
                        isDarkMode 
                          ? 'bg-gradient-to-br from-[#2a2a2a] via-[#1a1a1a] to-[#2a2a2a] border border-gray-700/50 shadow-lg shadow-black/30' 
                          : 'bg-white border border-gray-200'
                      }`}>
                        {salesStatuses.map(s => {
                          const count = sales.filter(o => s.key === 'all' || (o.status && o.status.toLowerCase() === s.key)).length;
                          if (s.key !== 'all' && count === 0) return null;
                          return (
                            <button
                              key={s.key}
                              className={`px-4 sm:px-6 py-2.5 rounded-lg font-semibold text-sm sm:text-base transition-all duration-200 ${
                                salesStatus === s.key 
                                  ? 'bg-red-600 text-white shadow-md' 
                                  : isDarkMode 
                                    ? 'bg-gray-700 text-gray-200 hover:bg-gray-600 border border-gray-600' 
                                    : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200'
                              }`}
                              onClick={() => setSalesStatus(s.key)}
                            >
                              {s.label} ({count})
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    {/* My Sales tab skeleton loading */}
                    {loadingSales ? (
                      <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
                        {[...Array(6)].map((_, i) => (
                          <div key={i} className={`rounded-2xl overflow-hidden border animate-pulse transition-colors duration-300 ${
                            isDarkMode ? 'bg-[#2a2a2a] border-gray-700/50' : 'bg-white border-gray-100'
                          }`}>
                            <div className={`${isDarkMode ? 'bg-gray-800/50' : 'bg-gray-100'} h-12`} />
                            <div className="flex gap-4 p-4">
                              <div className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'} w-20 h-20 rounded-lg`} />
                              <div className="flex-1 space-y-3">
                                <div className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'} h-5 w-3/4 rounded`} />
                                <div className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'} h-4 w-1/2 rounded`} />
                                <div className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'} h-6 w-24 rounded`} />
                              </div>
                            </div>
                            <div className={`${isDarkMode ? 'bg-gray-800/50' : 'bg-gray-100'} h-14 flex items-center justify-between px-4`}>
                              <div className="flex items-center gap-3">
                                <div className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'} w-8 h-8 rounded-full`} />
                                <div className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'} h-4 w-24 rounded`} />
                              </div>
                              <div className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'} h-9 w-28 rounded-xl`} />
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : errorSales ? (
                      <div className="text-red-600">{errorSales}</div>
                    ) : sales.length === 0 ? (
                      <div className={`flex flex-col items-center justify-center py-16 rounded-xl shadow-none border-none mx-auto max-w-lg mt-8 transition-all duration-300 ${
                        isDarkMode 
                          ? 'bg-[#2a2a2a] shadow-2xl shadow-black/50 border border-gray-700/50' 
                          : 'bg-white'
                      }`}>
                        {/* Shopping bags SVG icon */}
                        <svg width="80" height="80" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="mb-4">
                          <rect x="14" y="20" width="24" height="28" rx="3" fill="#F87171" stroke="#111" strokeWidth="2"/>
                          <rect x="28" y="28" width="18" height="20" rx="2.5" fill="#FBBF24" stroke="#111" strokeWidth="2"/>
                          <path d="M20 20V16a8 8 0 0116 0v4" stroke="#111" strokeWidth="2"/>
                          <circle cx="22" cy="32" r="2" fill="#fff" stroke="#111" strokeWidth="1.5"/>
                          <circle cx="34" cy="32" r="2" fill="#fff" stroke="#111" strokeWidth="1.5"/>
                        </svg>
                        <div className={`text-xl font-bold mb-1 text-center transition-colors duration-300 ${
                          isDarkMode ? 'text-white' : 'text-gray-900'
                        }`}>
                          No sales found.
                        </div>
                        <div className={`text-base text-center transition-colors duration-300 ${
                          isDarkMode ? 'text-gray-400' : 'text-gray-500'
                        }`}>
                          Your sales will be displayed here when available.
                        </div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
                        {sales
                          .filter(o => salesStatus === 'all' || (o.status && o.status.toLowerCase().includes(salesStatus)))
                          .slice(0, salesToShow)
                          .map((o, idx) => (
                          <div key={o._id || o.order_id || idx} className={`rounded-2xl shadow overflow-hidden border flex flex-col transition-all duration-500 ${
                            isDarkMode 
                              ? 'bg-[#2a2a2a] shadow-2xl shadow-black/50 border-gray-700/50' 
                              : 'bg-white border-gray-100'
                          }`}>
                            <div className={`flex items-center justify-between px-4 py-2 transition-colors duration-300 ${
                              isDarkMode ? 'bg-gray-800/50' : 'bg-indigo-50'
                            }`}>
                              <span className={`font-semibold transition-colors duration-300 ${
                                isDarkMode ? 'text-gray-200' : 'text-gray-700'
                              }`}>#{o.order_id || o.order}</span>
                              <span className={`text-sm transition-colors duration-300 ${
                                isDarkMode ? 'text-gray-400' : 'text-gray-400'
                              }`}>{o.date ? new Date(o.date).toLocaleDateString('en-GB') : ''}</span>
                            </div>
                            <div className="flex flex-row items-start gap-4 px-4 py-4 flex-1">
                              {/* Left: Image and Status */}
                              <div className="flex flex-col items-start gap-2 min-w-[90px]">
                                <a
                                  href={getProductPath(o)}
                                  onClick={(e) => openProductLink(o, e)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ display: 'block', width: '100%' }}
                                >
                                  <img
                                    src={getImageUrl(o.product_main_image) || 'https://placehold.co/80x80'}
                                    alt={o.product_title || o.product}
                                    className="w-20 h-20 rounded-lg object-cover mb-1"
                                    style={{ marginBottom: 4 }}
                                    onError={(e) => {
                                      e.target.src = 'https://placehold.co/80x80';
                                    }}
                                  />
                                </a>
                                {o.status && o.status !== 'all' && (
                                  <span className={`px-4 py-1 rounded-full text-sm font-semibold mt-1 transition-colors duration-300 ${
                                    o.status.toLowerCase().includes('onhold') ? (isDarkMode ? 'bg-yellow-900/50 text-yellow-200' : 'bg-yellow-100 text-yellow-800') :
                                    o.status.toLowerCase().includes('ready') ? (isDarkMode ? 'bg-pink-900/50 text-pink-200' : 'bg-pink-100 text-pink-700') :
                                    o.status.toLowerCase().includes('cancelled') ? (isDarkMode ? 'bg-red-900/50 text-red-200' : 'bg-red-100 text-red-700') :
                                    o.status.toLowerCase().includes('agent') ? (isDarkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-700') :
                                    o.status.toLowerCase().includes('processing') ? (isDarkMode ? 'bg-blue-900/50 text-blue-200' : 'bg-blue-100 text-blue-700') :
                                    o.status.toLowerCase().includes('completed') ? (isDarkMode ? 'bg-green-900/50 text-green-200' : 'bg-green-100 text-green-700') :
                                    (isDarkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-200 text-gray-700')
                                  }`} style={{marginLeft: 0}}>{o.statusLabel || o.status}</span>
                                )}
                              </div>
                              {/* Right: Name and Price, fixed width and ellipsis */}
                              <div className="flex-1 flex flex-col items-start justify-start pl-2 pt-1 max-w-[180px]">
                                <a
                                  href={getProductPath(o)}
                                  onClick={(e) => openProductLink(o, e)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className={`font-bold text-lg mb-1 truncate w-full hover:text-red-600 transition-colors duration-300 ${
                                    isDarkMode ? 'text-white' : 'text-gray-900'
                                  }`}
                                  style={{whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block'}}
                                  title={o.product_title || o.product}
                                >
                                  {o.product_title || o.product}
                                </a>
                                <div className="text-blue-700 font-bold text-lg mb-2">
                                  {o.salePrice ? (
                                    <span>${Number(o.salePrice).toFixed(2)}</span>
                                  ) : o.total ? (
                                    <span>${Number(o.total).toFixed(2)}</span>
                                  ) : o.price ? (
                                    <span>${Number(o.price).toFixed(2)}</span>
                                  ) : ''}
                                </div>
                              </div>
                            </div>
                            <div className={`flex items-center px-4 py-3 transition-colors duration-300 ${
                              isDarkMode ? 'bg-gray-800/50' : 'bg-indigo-50'
                            }`}>
                              <div className="flex items-center gap-2 flex-1">
                                {(() => {
                                  const profileUrl = o.buyerUsername ? `/profile/${o.buyerUsername}` : '#';
                                  return (
                                    <>
                                      <a
                                        href={profileUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ display: 'inline-block' }}
                                      >
                                        {o.buyer_avatar ? (
                                          <img src={o.buyer_avatar} alt={o.accountUsername || ''} className="w-8 h-8 rounded-full object-cover" />
                                        ) : (
                                          <img 
                                            src={generateModernAvatar(o.accountUsername || 'User')} 
                                            alt={o.accountUsername || 'User'} 
                                            className="w-8 h-8 rounded-full object-cover border-2 border-white" 
                                          />
                                        )}
                                      </a>
                                      <a
                                        href={profileUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={`text-sm font-semibold hover:text-red-600 transition-colors duration-300 ${
                                          isDarkMode ? 'text-gray-300' : 'text-gray-700'
                                        }`}
                                        style={{ marginLeft: 4 }}
                                      >
                                        {o.buyerUsername || ''}
                                      </a>
                                    </>
                                  );
                                })()}
                              </div>
                              <button className="px-6 py-2 rounded-xl bg-red-600 text-white font-semibold hover:bg-red-700 transition ml-auto" onClick={() => setOrderModal(o)}>View Details</button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {(!loadingSales && sales.filter(o => salesStatus === 'all' || (o.status && o.status.toLowerCase().includes(salesStatus))).length > salesToShow) && (
                      <div className="flex justify-center mt-6">
                        <button
                          className="px-6 py-2 rounded-full bg-red-600 text-white font-semibold hover:bg-red-700 transition"
                          onClick={() => setSalesToShow(salesToShow + 6)}
                        >
                          Load More
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
              {tab === 'purchases' && (
                <div>
                  <div className="mb-8">
                    <div className="text-2xl font-bold mb-4">My Purchases</div>
                    {/* Purchases Status Bar */}
                    <div className="mb-8">
                      <div className={`inline-flex flex-wrap gap-2 sm:gap-3 rounded-xl px-3 sm:px-4 py-2 items-center shadow-sm transition-all duration-300 ${
                        isDarkMode 
                          ? 'bg-gradient-to-br from-[#2a2a2a] via-[#1a1a1a] to-[#2a2a2a] border border-gray-700/50 shadow-lg shadow-black/30' 
                          : 'bg-white border border-gray-200'
                      }`}>
                        {purchaseStatuses.map(s => {
                          const count = purchases.filter(o => s.key === 'all' || (o.status && o.status.toLowerCase() === s.key)).length;
                          if (s.key !== 'all' && count === 0) return null;
                          return (
                            <button
                              key={s.key}
                              className={`px-4 sm:px-6 py-2.5 rounded-lg font-semibold text-sm sm:text-base transition-all duration-200 ${
                                purchaseStatus === s.key 
                                  ? 'bg-red-600 text-white shadow-md' 
                                  : isDarkMode 
                                    ? 'bg-gray-700 text-gray-200 hover:bg-gray-600 border border-gray-600' 
                                    : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200'
                              }`}
                              onClick={() => setPurchaseStatus(s.key)}
                            >
                              {s.label} ({count})
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    {/* My Purchases tab skeleton loading */}
                    {loadingPurchases ? (
                      <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
                        {[...Array(6)].map((_, i) => (
                          <div key={i} className={`rounded-2xl overflow-hidden border animate-pulse transition-colors duration-300 ${
                            isDarkMode ? 'bg-[#2a2a2a] border-gray-700/50' : 'bg-white border-gray-100'
                          }`}>
                            <div className={`${isDarkMode ? 'bg-gray-800/50' : 'bg-gray-100'} h-12`} />
                            <div className="flex gap-4 p-4">
                              <div className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'} w-20 h-20 rounded-lg`} />
                              <div className="flex-1 space-y-3">
                                <div className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'} h-5 w-3/4 rounded`} />
                                <div className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'} h-4 w-1/2 rounded`} />
                                <div className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'} h-6 w-24 rounded`} />
                              </div>
                            </div>
                            <div className={`${isDarkMode ? 'bg-gray-800/50' : 'bg-gray-100'} h-14 flex items-center justify-between px-4`}>
                              <div className="flex items-center gap-3">
                                <div className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'} w-8 h-8 rounded-full`} />
                                <div className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'} h-4 w-24 rounded`} />
                              </div>
                              <div className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'} h-9 w-28 rounded-xl`} />
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : errorPurchases ? (
                      <div className="text-red-600">{errorPurchases}</div>
                    ) : purchases.length === 0 ? (
                      <div className={`flex flex-col items-center justify-center py-16 rounded-xl shadow-none border-none mx-auto max-w-lg mt-8 transition-all duration-300 ${
                        isDarkMode 
                          ? 'bg-[#2a2a2a] shadow-2xl shadow-black/50 border border-gray-700/50' 
                          : 'bg-white'
                      }`}>
                        {/* Shopping bags SVG icon */}
                        <svg width="80" height="80" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="mb-4">
                          <rect x="14" y="20" width="24" height="28" rx="3" fill="#F87171" stroke="#111" strokeWidth="2"/>
                          <rect x="28" y="28" width="18" height="20" rx="2.5" fill="#FBBF24" stroke="#111" strokeWidth="2"/>
                          <path d="M20 20V16a8 8 0 0116 0v4" stroke="#111" strokeWidth="2"/>
                          <circle cx="22" cy="32" r="2" fill="#fff" stroke="#111" strokeWidth="1.5"/>
                          <circle cx="34" cy="32" r="2" fill="#fff" stroke="#111" strokeWidth="1.5"/>
                        </svg>
                        <div className={`text-xl font-bold mb-1 text-center transition-colors duration-300 ${
                          isDarkMode ? 'text-white' : 'text-gray-900'
                        }`}>
                          No purchases found.
                        </div>
                        <div className={`text-base text-center transition-colors duration-300 ${
                          isDarkMode ? 'text-gray-400' : 'text-gray-500'
                        }`}>
                          Your purchases will be displayed here when available.
                        </div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
                        {purchases
                          .filter(s => purchaseStatus === 'all' || (s.status && s.status.toLowerCase().includes(purchaseStatus)))
                          .slice(0, purchasesToShow)
                          .map((s, idx) => (
                          <div key={s._id || s.order_id || idx} className={`rounded-2xl shadow overflow-hidden border flex flex-col transition-all duration-500 ${
                            isDarkMode 
                              ? 'bg-[#2a2a2a] shadow-2xl shadow-black/50 border-gray-700/50' 
                              : 'bg-white border-gray-100'
                          }`}>
                            <div className={`flex items-center justify-between px-4 py-2 transition-colors duration-300 ${
                              isDarkMode ? 'bg-gray-800/50' : 'bg-indigo-50'
                            }`}>
                              <span className={`font-semibold transition-colors duration-300 ${
                                isDarkMode ? 'text-gray-200' : 'text-gray-700'
                              }`}>#{s.order_id || s.order}</span>
                              <span className={`text-sm transition-colors duration-300 ${
                                isDarkMode ? 'text-gray-400' : 'text-gray-400'
                              }`}>{s.date ? new Date(s.date).toLocaleDateString('en-GB') : (s.date ? s.date.split('-').reverse().join('/') : '')}</span>
                            </div>
                            <div className="flex flex-row items-start gap-4 px-4 py-4 flex-1">
                              {/* Left: Image and Status */}
                              <div className="flex flex-col items-start gap-2 min-w-[90px]">
                                <a
                                  href={getProductPath(s)}
                                  onClick={(e) => openProductLink(s, e)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ display: 'block', width: '100%' }}
                                >
                                  <img
                                    src={getImageUrl(s.product_main_image) || 'https://placehold.co/80x80'}
                                    alt={s.product_title || s.product}
                                    className="w-20 h-20 rounded-lg object-cover mb-1"
                                    style={{ marginBottom: 4 }}
                                    onError={(e) => {
                                      e.target.src = 'https://placehold.co/80x80';
                                    }}
                                  />
                                </a>
                                {s.status && s.status !== 'all' && (
                                  <span className={`px-4 py-1 rounded-full text-sm font-semibold mt-1 transition-colors duration-300 ${
                                    s.status.toLowerCase().includes('onhold') ? (isDarkMode ? 'bg-yellow-900/50 text-yellow-200' : 'bg-yellow-100 text-yellow-800') :
                                    s.status.toLowerCase().includes('completed') ? (isDarkMode ? 'bg-green-900/50 text-green-200' : 'bg-green-100 text-green-700') :
                                    s.status.toLowerCase().includes('wrong') ? (isDarkMode ? 'bg-red-900/50 text-red-200' : 'bg-red-100 text-red-700') :
                                    (isDarkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-200 text-gray-700')
                                  }`} style={{marginLeft: 0}}>{s.statusLabel || s.status}</span>
                                )}
                              </div>
                              {/* Right: Name and Price, fixed width and ellipsis */}
                              <div className="flex-1 flex flex-col items-start justify-start pl-2 pt-1 max-w-[180px]">
                                <a
                                  href={getProductPath(s)}
                                  onClick={(e) => openProductLink(s, e)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className={`font-bold text-lg mb-1 truncate w-full hover:text-red-600 transition-colors duration-300 ${
                                    isDarkMode ? 'text-white' : 'text-gray-900'
                                  }`}
                                  style={{whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block'}}
                                  title={s.product_title || s.product}
                                >
                                  {s.product_title || s.product}
                                </a>
                                <div className="text-blue-700 font-bold text-lg mb-2">
                                  {s.salePrice ? (
                                    <span>${Number(s.salePrice).toFixed(2)}</span>
                                  ) : s.total ? (
                                    <span>${Number(s.total).toFixed(2)}</span>
                                  ) : s.price ? (
                                    <span>${Number(s.price).toFixed(2)}</span>
                                  ) : ''}
                                </div>
                              </div>
                            </div>
                            <div className={`flex items-center px-4 py-3 transition-colors duration-300 ${
                              isDarkMode ? 'bg-gray-800/50' : 'bg-indigo-50'
                            }`}>
                              <div className="flex items-center gap-2 flex-1">
                                {(() => {
                                  const profileUrl = s.seller_name ? `/profile/${s.seller_name}` : '#';
                                  return (
                                    <>
                                      <a
                                        href={profileUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ display: 'inline-block' }}
                                      >
                                        {s.seller_avatar || s.sellerAvatar ? (
                                          <img src={s.seller_avatar || s.sellerAvatar} alt={s.seller_name || s.seller || ''} className="w-8 h-8 rounded-full object-cover" />
                                        ) : (
                                          <img 
                                            src={generateModernAvatar(s.seller_name || s.seller || 'Seller')} 
                                            alt={s.seller_name || s.seller || 'Seller'} 
                                            className="w-8 h-8 rounded-full object-cover border-2 border-white" 
                                          />
                                        )}
                                      </a>
                                      <a
                                        href={profileUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={`text-sm font-semibold hover:text-red-600 transition-colors duration-300 ${
                                          isDarkMode ? 'text-gray-300' : 'text-gray-700'
                                        }`}
                                        style={{ marginLeft: 4 }}
                                      >
                                        {s.seller_name || ''}
                                      </a>
                                    </>
                                  );
                                })()}
                              </div>
                              <button className="px-6 py-2 rounded-xl bg-red-600 text-white font-semibold hover:bg-red-700 transition ml-auto">View Details</button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {(!loadingPurchases && purchases.filter(s => purchaseStatus === 'all' || (s.status && s.status.toLowerCase().includes(purchaseStatus))).length > purchasesToShow) && (
                      <div className="flex justify-center mt-6">
                        <button
                          className="px-6 py-2 rounded-full bg-red-600 text-white font-semibold hover:bg-red-700 transition"
                          onClick={() => setPurchasesToShow(purchasesToShow + 6)}
                        >
                          Load More
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* Order Details Modal for My Sales tab */}
      {orderModal && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className={`rounded-2xl shadow-xl p-8 w-full max-w-3xl relative transition-all duration-500 ${
            isDarkMode 
              ? 'bg-[#2a2a2a] shadow-2xl shadow-black/50 border border-gray-700/50' 
              : 'bg-white'
          }`}>
            <button className={`absolute top-2 right-2 text-2xl transition-colors duration-300 ${
              isDarkMode ? 'text-gray-400 hover:text-red-400' : 'text-gray-400 hover:text-red-500'
            }`} onClick={() => setOrderModal(null)}>&times;</button>
            <h3 className={`text-2xl font-bold mb-4 flex items-center gap-2 transition-colors duration-300 ${
              isDarkMode ? 'text-white' : 'text-black'
            }`}>
              <span className={`inline-block rounded-full p-2 transition-colors duration-300 ${
                isDarkMode ? 'bg-red-900/50 text-red-400' : 'bg-red-100 text-red-600'
              }`}><svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M6 6h15l-1.5 9h-13z"/><circle cx="9" cy="21" r="1"/><circle cx="19" cy="21" r="1"/></svg></span>
              Order #{orderModal.order_id}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-6">
              <div>
                <div className={`mb-2 transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-300' : 'text-black'
                }`}><b>Order Number:</b> #{orderModal.order_id}</div>
                <div className={`mb-2 transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-300' : 'text-black'
                }`}><b>Order Date:</b> {orderModal.date ? new Date(orderModal.date).toLocaleString('en-GB') : ''}</div>
                <div className={`mb-2 transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-300' : 'text-black'
                }`}><b>Status:</b> <span className="font-semibold text-green-500">{orderModal.status}</span></div>
              </div>
              <div className={`rounded-xl p-4 flex flex-col gap-2 transition-all duration-300 ${
                isDarkMode ? 'bg-gray-800/50 border border-gray-700/50' : 'bg-gray-50'
              }`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    {orderModal.buyer_avatar ? (
                      <img src={orderModal.buyer_avatar} alt={orderModal.buyerUsername || ''} className="w-10 h-10 rounded-full object-cover" />
                    ) : (
                      <img 
                        src={generateModernAvatar(orderModal.buyerUsername || 'User')} 
                        alt={orderModal.buyerUsername || 'User'} 
                        className="w-10 h-10 rounded-full object-cover border-2 border-white" 
                      />
                    )}
                    <div>
                      <div className={`font-semibold transition-colors duration-300 ${
                        isDarkMode ? 'text-gray-300' : 'text-black'
                      }`}>Buyer:</div>
                      <div className={`transition-colors duration-300 ${
                        isDarkMode ? 'text-white' : 'text-black'
                      }`}>{orderModal.buyerUsername || ''}</div>
                    </div>
                  </div>
                  <button className={`border px-4 py-2 rounded-lg font-semibold transition-colors duration-300 ${
                    isDarkMode 
                      ? 'border-red-500 text-red-400 hover:bg-red-900/50' 
                      : 'border-red-500 text-red-500 hover:bg-red-50'
                  }`}>Contact Buyer</button>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-6">
              <div className="flex gap-4 items-center">
                <img
                  src={getImageUrl(orderModal.product_main_image) || 'https://placehold.co/80x80'}
                  alt={orderModal.product_title}
                  className="w-24 h-24 rounded-lg object-cover"
                  onError={(e) => {
                    e.target.src = 'https://placehold.co/80x80';
                  }}
                />
                <div>
                  <div className={`font-bold text-lg mb-1 transition-colors duration-300 ${
                    isDarkMode ? 'text-white' : 'text-black'
                  }`}>{orderModal.product_title}</div>
                  <div className={`text-sm mb-1 transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-400' : 'text-gray-500'
                  }`}>Seller: <span className={`font-semibold transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-300' : 'text-black'
                  }`}>{orderModal.seller_name}</span></div>
                  <div className="text-blue-500 font-bold text-lg">${orderModal.total ? orderModal.total.toFixed(2) : ''}</div>
                </div>
              </div>
              <div className={`rounded-xl p-4 flex flex-col gap-2 transition-all duration-300 ${
                isDarkMode ? 'bg-gray-800/50 border border-gray-700/50' : 'bg-gray-50'
              }`}>
                <div className={`transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-300' : 'text-black'
                }`}><b>Order Notes:</b> <span className={`transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-600'
                }`}>{orderModal.order_notes || '—'}</span></div>
                <div className={`transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-300' : 'text-black'
                }`}><b>Subtotal:</b> USD ${orderModal.total ? orderModal.total.toFixed(2) : ''}</div>
                <div className={`transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-300' : 'text-black'
                }`}><b>Total:</b> USD ${orderModal.total ? orderModal.total.toFixed(2) : ''}</div>
                <div className={`transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-300' : 'text-black'
                }`}><b>Payment Method:</b> <span className={`transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-600'
                }`}>{orderModal.payment_method || '—'}</span></div>
              </div>
            </div>
            <div className="flex justify-end">
              <button className={`px-6 py-2 rounded-xl font-semibold transition-colors duration-300 ${
                isDarkMode 
                  ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' 
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`} onClick={() => setOrderModal(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
      
      {/* Verification Popup */}
      {showVerificationPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className={`relative w-full max-w-md sm:max-w-lg lg:max-w-xl mx-auto rounded-2xl shadow-2xl transition-all duration-300 ${
            isDarkMode 
              ? 'bg-gradient-to-br from-gray-800 via-gray-900 to-gray-800 border border-gray-700/50' 
              : 'bg-white border border-gray-200'
          }`}>
            {/* Header with close button */}
            <div className="flex items-center justify-between p-4 sm:p-5 lg:p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-600 rounded-full flex items-center justify-center shadow-lg">
                  <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                  </svg>
                </div>
                <div>
                  <h3 className={`text-lg sm:text-xl font-bold transition-colors duration-300 ${
                    isDarkMode ? 'text-white' : 'text-gray-900'
                  }`}>Verified User</h3>
                  <p className={`text-sm transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-400' : 'text-gray-600'
                  }`}>Identity Confirmed</p>
                </div>
              </div>
              <button
                onClick={() => setShowVerificationPopup(false)}
                className={`p-2 rounded-full transition-all duration-200 hover:bg-gray-100 dark:hover:bg-gray-700 ${
                  isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="p-4 sm:p-5 lg:p-6">
              <div className={`text-sm sm:text-base leading-relaxed transition-colors duration-300 ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>
                <div className={`p-3 sm:p-4 rounded-xl border transition-colors duration-300 ${
                  isDarkMode 
                    ? 'bg-gray-800/50 border-gray-700/50' 
                    : 'bg-gray-50 border-gray-200'
                }`}>
                  <div className="mb-2">
                    <span className={`text-sm font-semibold transition-colors duration-300 ${
                      isDarkMode ? 'text-green-400' : 'text-green-600'
                    }`}>Verification Complete</span>
                  </div>
                  <p className={`text-xs sm:text-sm transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-400' : 'text-gray-600'
                  }`}>
                    You have provided your ID details and a selfie video to help us confirm your identity. We may share this information with law enforcement if fraudulent activity occurs.
                  </p>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 p-4 sm:p-5 lg:p-6 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setShowVerificationPopup(false)}
                className={`px-4 sm:px-5 py-2 sm:py-2.5 rounded-lg font-semibold transition-all duration-200 ${
                  isDarkMode
                    ? 'bg-gray-700 hover:bg-gray-600 text-white border border-gray-600 hover:border-gray-500'
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300 hover:border-gray-400'
                }`}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      
      <Footer />
    </>
  );
}